import * as React from "react"
import { Container, Row, Col, Table, Spinner, Form, Button } from "react-bootstrap"
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map } from "rxjs/operators"
import { toast } from "react-toastify"
import { format, parseISO } from "date-fns"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import TablePagination from "../TablePagination"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import { OrphanedGridGet, OrphanedAccept, OrphanedDelete, OrphanedRecordsCount } from "../../api/OrphanedResults/OrphanedProcess"
import OrphanedGridResponse from "../../api/OrphanedResults/Models/OrphanedGridResponse"
import OrphanedRow from "../../api/OrphanedResults/Models/OrphanedRow"
import ConfirmationModal from "../ConfirmationModal"
import Image from "../../assets/images/CCF.png"
import CompanyInput from "../FormattedInputs/CompanyInput"
import Checkbox from "../Checkbox"
import { Filter, Sort } from "../../api/GridRequest"
import { getOrphanedResultsCount, setOrphanedResultsCount} from "../../storage/LogInPortals"

interface OrphanedResultsState {
  loading: boolean
  data?: OrphanedGridResponse
  selectAll: boolean
  selectedList: { TestDetailID: number | string, AccountID?: number, TestDetailClinicalID: number | string }[]
  confirmModal: boolean
  recordCount:number
}

interface OrphanedResultsSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
}

const PageSize = 10

type OrphanedColumn = keyof OrphanedRow | "Company" | "Image" | "Checked"

const DefaultColumns: { name: OrphanedColumn, label: string }[] = [
  { name: "Checked", label: "" },
  { name: "DonorID", label: "Donor ID" },
  { name: "FirstName", label: "First Name" },
  { name: "LastName", label: "Last Name" },
  { name: "Company", label: "Company" },
  { name: "Reason", label: "Reason" },
  { name: "TestType", label: "Type" },
  { name: "ReferenceID", label: "Reference" },
  { name: "LabAccountNumber", label: "Lab Account" },
  { name: "DateCollected", label: "Collected" },
  { name: "Image", label: "Image" }
]

export default class OrphanedResults extends React.PureComponent<{}, OrphanedResultsState> {
  state: OrphanedResultsState = {
    loading: false,
    selectAll: false,
    selectedList: [],
    confirmModal: false,
    recordCount:0
  }

  page$ = new BehaviorSubject<OrphanedResultsSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: []
  })
  pageSubscription?: Subscription
  actionsSubscription?: Subscription

  componentDidMount() {
    //this.recordCountGet()
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        OrphanedGridGet(params.pageNumber, PageSize, params.sort, params.filters).pipe(
          map((result: OrphanedGridResponse) => ({ data: result, loading: false })))
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: OrphanedGridResponse }) => {
        if (newState.data) {
          setOrphanedResultsCount(newState.data.Total)
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          setOrphanedResultsCount(0)
          this.setState({ loading: newState.loading, selectedList: [], selectAll: false })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.actionsSubscription && this.actionsSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  pageChanged = (pageNumber: number) => {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  sortGrid(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.page$.next({ ...this.page$.value, sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  convertDate = (date?: string) => {
    if (date) {
      return format(parseISO(date), "MM/dd/yyyy")
    } else {
      return ""
    }
  }

  handleAllTestDetailCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    const tests = checked && this.state.data ? this.state.data.Records.map((row: OrphanedRow) => ({ TestDetailID: row.TestDetailID, TestDetailClinicalID: row. TestDetailClinicalID })) : []
    this.setState({ selectedList: tests, selectAll: checked })
  }

  handleTestDetailCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    let tests = [...this.state.selectedList]

    const index = tests.findIndex(test => test.TestDetailID === value)
    if (index > -1) {
      tests = [...tests.slice(0, index), ...tests.slice(index + 1)]
    } else {
      tests.push({ TestDetailID: value, TestDetailClinicalID: 0 })
    }
    this.setState({ selectedList: tests })
  }

  handleCompanyChange = (id: number, selected?: number, clinicalId?: number) => {
    if (this.testDetailSelected(id)) {
      let tests = [...this.state.selectedList]

      const index = tests.findIndex(test => test.TestDetailID === id)
      tests[index] = { TestDetailID: id, AccountID: selected, TestDetailClinicalID: clinicalId || 0}
      this.setState({ selectedList: tests })
    }
  }

  handleCancel = () => {
    //TODO
  }

  handleDelete = () => {
    this.setState({ confirmModal: true })
  }

  handleAccept = () => {
    this.actionsSubscription = OrphanedAccept(this.state.selectedList).subscribe(
      data => {
        toast.success(data)
        this.page$.next({ ...this.page$.value, pageNumber: 1 })
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  hideModal = () => {
    this.setState({ confirmModal: false })
  }

  confirmDelete = () => {
    this.hideModal()
    this.actionsSubscription = OrphanedDelete(this.state.selectedList.map(item => item.TestDetailID) as string[]).subscribe(
      data => {
        toast.success(data)
        this.page$.next({ ...this.page$.value, pageNumber: 1 })
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error, stateChanges: object) => {
    this.setState(stateChanges)
    toast.error(error)
  }

  renderColumns = () => {
    return DefaultColumns.map((col) => {
      if (col.name === "Checked") {
        return <th className="text-center font-weight-normal" key={col.name}>
          <Checkbox id="check_head" label={col.label} onChange={this.handleAllTestDetailCheck} checked={this.state.selectAll} />
        </th>
      }
      const sort = this.page$.value.sort
      return (
        <th className="text-nowrap font-weight-normal" key={col.name}>
          {this.state.data && col.name in this.state.data && <ColumnHeaderFilter
            title={col.label}
            sort={sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
            items={this.state.data[col.name]}
            onSort={(order?: string) => this.sortGrid(col.name, order)}
            onFilter={(items: string[]) => this.filterGrid(col.name, items)}
          />}
          {this.state.data && !(col.name in this.state.data) && (col.label.toUpperCase())}
        </th>
      )
    })
  }

  testDetailSelected = (id: number) => {
    return this.state.selectedList.findIndex(test => test.TestDetailID === id) > -1
  }

  renderCell = (row: OrphanedRow, colName: OrphanedColumn) => {
    switch (colName) {
      case "Checked":
        return <td className="text-center" key={row.TestDetailID}>
          <Checkbox id={`check_${row.TestDetailID}`} label="" value={row.TestDetailID.toString()} checked={this.testDetailSelected(row.TestDetailID)} onChange={this.handleTestDetailCheck} />
        </td>
      case "Company":
        return <td key={`${colName}_${row.TestDetailID}`} className="typeahead-dropdown">
          <CompanyInput 
            id={ `companySearch_${row.TestDetailID}` }
            onChange={ selected => this.handleCompanyChange(row.TestDetailID, selected[0]?.AccountID) }
            disabled={ !this.testDetailSelected(row.TestDetailID) } />
        </td>
      case "Image":
        return <td key={`${colName}_${row.TestDetailID}`}>
          {row.TestRAImageID > 0 && <img src={Image} alt="" />}
        </td>
      case "DateCollected":
        return <td key={`${colName}_${row.TestDetailID}`}>{this.convertDate(row.DateCollected)}</td>
      default:
        return <td key={`${colName}_${row.TestDetailID}`}>{row[colName]}</td>
    }
  }

  renderRows = () => {
    if (this.state.data && this.state.data.Records.length) {
      return this.state.data.Records.map((row: OrphanedRow, i: number) =>
        <tr key={i}>
          {DefaultColumns.map((col) => this.renderCell(row, col.name))}
        </tr>)
    } else {
      return <tr><td colSpan={DefaultColumns.length}>No Records Found</td></tr>
    }
  }

  render() {
    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        <Container fluid className="grid-container">
          <Row>
            <Col>
              <Table bordered responsive>
                <thead className="thead-light">
                  <tr>
                    {this.renderColumns()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr>
                        <td className="text-center" colSpan={DefaultColumns.length}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr> : this.renderRows()
                  }
                </tbody>
              </Table>
            </Col>
          </Row>
          {this.state.data ?
            <Row>
              <Col>
                <span>Total records: {this.state.data.Total}</span>
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />
              </Col>
            </Row> : undefined}
          <Row>
            <Col className="text-right mb-2">
              <Button variant="secondary" onClick={this.handleCancel} className="mr-2">
                Cancel
              </Button>
              <Button variant="primary" onClick={this.handleDelete} disabled={this.state.selectedList.length === 0} className="mr-2">
                Delete
              </Button>
              <Button variant="primary" onClick={this.handleAccept} disabled={this.state.selectedList.some((test => test.AccountID === undefined))}>
                Accept
              </Button>
              <ConfirmationModal show={this.state.confirmModal}
                title="CONFIRM REMOVE"
                textTitle="Are you sure you want to remove selected record?"
                text="Selected record will be removed permanently."
                onConfirm={this.confirmDelete}
                onHide={this.hideModal} />
            </Col>
          </Row>
        </Container>
      </AppPageWrapper>
    )
  }
}
